/**
 * Abertura animada do app nativo ("cresce e abre").
 *
 * O splash nativo é só branco, então o primeiro frame daqui também é. Uma
 * bolinha laranja nasce no centro, pega impulso, cresce com mola até virar o
 * ícone e o "p" sobe de dentro dele. Na saída o ícone recua, o "p" sai por
 * cima, o laranja toma a tela e se abre em duas metades de borda curva (a de
 * baixo primeiro), revelando o app.
 *
 * Tudo roda na UI thread: um relógio (`clock`, em ms da linha do tempo) anda
 * de 0 até o fim e cada elemento aplica a própria bezier ou mola sobre ele. O
 * relógio é linear porque é tempo; nenhum movimento na tela é linear.
 *
 * Web não passa por aqui: o export estático precisa do HTML sem nada por cima.
 */
import { SplashScreen } from 'expo-router';
import React, { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Image, type LayoutChangeEvent, Platform, StyleSheet, View } from 'react-native';
import Animated, {
  cancelAnimation,
  Easing,
  type SharedValue,
  useAnimatedProps,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withDelay,
  withRepeat,
  withTiming,
} from 'react-native-reanimated';
import Svg, { Path } from 'react-native-svg';
import { scheduleOnRN } from 'react-native-worklets';

const AnimatedPath = Animated.createAnimatedComponent(Path);

// ─── Marca e geometria ─────────────────────────────────────────────────────

const BRAND = '#FD5001';
const PAPER = '#FFFFFF';

/** O "p" do favicon sem o quadrado laranja. */
const MARK_SOURCE = require('../assets/images/splash-mark.png');
const MARK_ASPECT = 600 / 607;

/** Lado do ícone quando assenta. */
const ICON = 148;
/** Bolinha inicial. */
const DOT = 56;
/** Raio do quadrado ÷ lado (o ícone do app). */
const ICON_RADIUS = 0.2;
/** Quanto a borda curva da abertura arqueia para baixo. */
const SPLIT_BOW = 90;
/** Folga para as formas passarem da borda da tela. */
const BLEED = 20;

// ─── Coreografia (ms desde que o overlay substitui o splash nativo) ───────

type Beat = { start: number; dur: number };
type SpringBeat = { start: number; stiffness: number; damping: number; settle: number };

const BEAT = {
  /** A bolinha nasce passando um pouco do tamanho. */
  pop: { start: 0, dur: 380 },
  /** Encolhe antes de crescer (antecipação). */
  crouch: { start: 380, dur: 140 },
  /** Cresce com mola até o ícone e deixa de ser círculo. */
  grow: { start: 520, stiffness: 240, damping: 15, settle: 700 } as SpringBeat,
  /** Estica na horizontal enquanto cresce, depois volta. */
  stretch: { start: 520, dur: 320 },
  /** O "p" sobe de dentro do ícone, girando, e assenta. */
  rise: { start: 760, stiffness: 200, damping: 14, settle: 700 } as SpringBeat,
  /** Aceno do ícone. */
  nod: { start: 1180, dur: 260 },
  /** Saída: o ícone recua antes de tomar a tela. */
  windup: { start: 1400, dur: 140 },
  /** O "p" sai voando por cima. */
  markOut: { start: 1480, dur: 320 },
  /** O laranja cresce até cobrir a tela. */
  fill: { start: 1500, dur: 380 },
  /** A metade de baixo desce... */
  splitBottom: { start: 1840, dur: 440 },
  /** ...e a de cima sobe logo depois. */
  splitTop: { start: 1900, dur: 440 },
};

/** Onde a linha do tempo espera o app ficar pronto (respira enquanto espera). */
const INTRO_END = BEAT.windup.start;
const OUTRO_END = BEAT.splitTop.start + BEAT.splitTop.dur;
/** O papel branco sai quando o laranja já cobre a tela; daí embaixo é o app. */
const PAPER_OFF = BEAT.fill.start + BEAT.fill.dur * 0.9;

const IDLE = { period: 1600, fadeIn: 400, fadeOut: 240 };
const REDUCED = { hold: 150, fade: 320 };
/** Se a tela nunca for medida, o splash nativo sai mesmo assim. */
const NATIVE_HIDE_FALLBACK_MS = 700;

// Curvas. Nomes dizem o gesto, não a fórmula.
const EASE_POP = Easing.bezierFn(0.34, 1.7, 0.64, 1);
const EASE_IN_OUT = Easing.bezierFn(0.65, 0, 0.35, 1);
const EASE_IN = Easing.bezierFn(0.55, 0, 0.9, 0.3);
const EASE_OUT = Easing.bezierFn(0.22, 1, 0.36, 1);
const EASE_FILL = Easing.bezierFn(0.8, 0, 0.2, 1);
const EASE_SPLIT = Easing.bezierFn(0.7, 0, 0.3, 1);

// ─── Worklets de tempo ────────────────────────────────────────────────────

function progress(t: number, b: Beat) {
  'worklet';
  return Math.min(1, Math.max(0, (t - b.start) / b.dur));
}

/** Mola amortecida analítica: 0 → 1 com passada, determinística para replay. */
function springAt(t: number, s: SpringBeat) {
  'worklet';
  const elapsed = t - s.start;
  if (elapsed <= 0) return 0;
  if (elapsed >= s.settle) return 1;
  const x = elapsed / 1000;
  const w0 = Math.sqrt(s.stiffness);
  const zeta = s.damping / (2 * w0);
  const wd = w0 * Math.sqrt(1 - zeta * zeta);
  return 1 - Math.exp(-zeta * w0 * x) * (Math.cos(wd * x) + ((zeta * w0) / wd) * Math.sin(wd * x));
}

/** Sobe e desce uma vez (0 → 1 → 0). */
function swell(p: number) {
  'worklet';
  return Math.sin(Math.PI * p);
}

function lerp(a: number, b: number, p: number) {
  'worklet';
  return a + (b - a) * p;
}

// ─── Sinal de "app pronto" (o layout avisa quando sai do próprio spinner) ─

let appReady = false;
const readyListeners = new Set<() => void>();

export function markAppReady() {
  if (appReady) return;
  appReady = true;
  readyListeners.forEach((listener) => listener());
}

export function useAppReady() {
  return useSyncExternalStore(
    (listener) => {
      readyListeners.add(listener);
      return () => readyListeners.delete(listener);
    },
    () => appReady,
    // Sem isto o export estático da web quebra: o React lança no render do
    // servidor e todas as páginas saem com o #root vazio.
    () => false,
  );
}

// ─── Componente ───────────────────────────────────────────────────────────

export type AnimatedSplashProps = {
  /** O app por baixo está pronto para aparecer. */
  ready: boolean;
  onFinish: () => void;
  /** Esconde o splash nativo no primeiro frame pintado. `false` no preview. */
  hideNativeSplash?: boolean;
  /** 1 = tempo real; 0.25 = câmera lenta para ajustar a coreografia. */
  speed?: number;
  /** Força (ou desliga) a versão de movimento reduzido; padrão segue o sistema. */
  reduceMotion?: boolean;
};

export function AnimatedSplash(props: AnimatedSplashProps) {
  if (Platform.OS === 'web') return null;
  return <SplashStage {...props} />;
}

type Size = { w: number; h: number };

function SplashStage({
  ready,
  onFinish,
  hideNativeSplash = true,
  speed = 1,
  reduceMotion,
}: AnimatedSplashProps) {
  const systemReduced = useReducedMotion();
  const reduced = reduceMotion ?? systemReduced;

  const [size, setSize] = useState<Size | null>(null);
  const [started, setStarted] = useState(false);
  const [introDone, setIntroDone] = useState(false);
  const [exiting, setExiting] = useState(false);
  const idleStarted = useRef(false);

  const clock = useSharedValue(0);
  const idleMix = useSharedValue(0);
  const idlePhase = useSharedValue(0);
  const fade = useSharedValue(1);

  // 1. Troca: o primeiro frame é branco como o nativo; só falta medir a tela.
  useEffect(() => {
    if (started) return;
    const begin = () => setStarted(true);
    if (!hideNativeSplash) {
      if (size) begin();
      return;
    }
    if (size) {
      SplashScreen.hideAsync().finally(begin);
      return;
    }
    const fallback = setTimeout(() => {
      SplashScreen.hideAsync().finally(begin);
    }, NATIVE_HIDE_FALLBACK_MS);
    return () => clearTimeout(fallback);
  }, [size, hideNativeSplash, started]);

  // 2. Abertura até o portão.
  useEffect(() => {
    if (!started || reduced) return;
    clock.value = withTiming(
      INTRO_END,
      { duration: INTRO_END / speed, easing: Easing.linear },
      (finished) => {
        if (finished) scheduleOnRN(setIntroDone, true);
      },
    );
  }, [started, reduced, speed, clock]);

  // 3. Portão: sai se o app estiver pronto; senão o ícone respira até ficar.
  useEffect(() => {
    if (reduced) {
      if (!started || !ready || exiting) return;
      setExiting(true);
      fade.value = withDelay(
        REDUCED.hold,
        withTiming(0, { duration: REDUCED.fade, easing: Easing.out(Easing.quad) }, (finished) => {
          if (finished) scheduleOnRN(onFinish);
        }),
      );
      return;
    }
    if (!introDone || exiting) return;
    if (!ready) {
      if (idleStarted.current) return;
      idleStarted.current = true;
      idleMix.value = withTiming(1, {
        duration: IDLE.fadeIn / speed,
        easing: Easing.inOut(Easing.sin),
      });
      idlePhase.value = withRepeat(
        withTiming(1, { duration: IDLE.period / speed, easing: Easing.inOut(Easing.sin) }),
        -1,
      );
      return;
    }
    setExiting(true);
    idleMix.value = withTiming(0, {
      duration: IDLE.fadeOut / speed,
      easing: Easing.out(Easing.quad),
    });
    clock.value = withTiming(
      OUTRO_END,
      { duration: (OUTRO_END - INTRO_END) / speed, easing: Easing.linear },
      (finished) => {
        if (finished) scheduleOnRN(onFinish);
      },
    );
  }, [reduced, started, ready, introDone, exiting, speed, onFinish, clock, idleMix, idlePhase, fade]);

  useEffect(
    () => () => {
      cancelAnimation(clock);
      cancelAnimation(idleMix);
      cancelAnimation(idlePhase);
      cancelAnimation(fade);
    },
    [clock, idleMix, idlePhase, fade],
  );

  const onLayout = (e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    if (!size || size.w !== width || size.h !== height) setSize({ w: width, h: height });
  };

  const rootStyle = useAnimatedStyle(() => ({ opacity: fade.value }));

  // O branco some quando o laranja já cobre a tela; embaixo dele está o app.
  const paperStyle = useAnimatedStyle(() => ({
    opacity: !reduced && clock.value >= PAPER_OFF ? 0 : 1,
  }));

  return (
    <Animated.View
      style={[StyleSheet.absoluteFill, rootStyle]}
      onLayout={onLayout}
      pointerEvents={exiting ? 'none' : 'auto'}
      accessibilityElementsHidden
      importantForAccessibility="no-hide-descendants"
    >
      <Animated.View style={[StyleSheet.absoluteFill, { backgroundColor: PAPER }, paperStyle]} />
      {reduced ? (
        <StaticIcon />
      ) : (
        <>
          <Icon size={size} clock={clock} idleMix={idleMix} idlePhase={idlePhase} />
          {size ? <Split size={size} clock={clock} /> : null}
        </>
      )}
    </Animated.View>
  );
}

// ─── Ícone: bolinha → quadrado → toma a tela ──────────────────────────────

function Icon({
  size,
  clock,
  idleMix,
  idlePhase,
}: {
  size: Size | null;
  clock: SharedValue<number>;
  idleMix: SharedValue<number>;
  idlePhase: SharedValue<number>;
}) {
  const screenW = size?.w ?? 0;
  const screenH = size?.h ?? 0;

  const shapeStyle = useAnimatedStyle(() => {
    const t = clock.value;
    if (t >= BEAT.splitBottom.start) return { width: 0, height: 0, borderRadius: 0, opacity: 0 };

    const pop = EASE_POP(progress(t, BEAT.pop));
    const crouch = EASE_IN_OUT(progress(t, BEAT.crouch));
    const grow = springAt(t, BEAT.grow);
    const beforeGrow = t < BEAT.grow.start;
    let side = beforeGrow ? DOT * pop * (1 - 0.18 * crouch) : lerp(DOT * 0.82, ICON, grow);
    const roundness = beforeGrow ? 0.5 : lerp(0.5, ICON_RADIUS, Math.min(1, grow));

    const st = swell(progress(t, BEAT.stretch));
    const sx = 1 + 0.14 * st;
    const sy = 1 - 0.12 * st;

    const nod = 1 - 0.07 * swell(EASE_OUT(progress(t, BEAT.nod)));
    const breathe = 1 - 0.05 * idleMix.value * swell(idlePhase.value);
    const wind = 1 - 0.1 * EASE_IN(progress(t, BEAT.windup));
    side *= nod * breathe * (t < BEAT.fill.start ? wind : 1);

    let w = side * sx;
    let h = side * sy;
    let r = side * roundness;
    if (t >= BEAT.fill.start) {
      const fill = EASE_FILL(progress(t, BEAT.fill));
      const base = ICON * 0.9;
      w = lerp(base, screenW + BLEED * 2, fill);
      h = lerp(base, screenH + BLEED * 2, fill);
      r = lerp(base * ICON_RADIUS, 0, fill);
    }
    return { width: Math.max(0, w), height: Math.max(0, h), borderRadius: Math.max(0, r), opacity: 1 };
  });

  const markStyle = useAnimatedStyle(() => {
    const t = clock.value;
    const rise = springAt(t, BEAT.rise);
    const out = EASE_IN(progress(t, BEAT.markOut));
    const pop = EASE_POP(progress(t, BEAT.pop));
    const grow = springAt(t, BEAT.grow);
    const side = t < BEAT.grow.start ? DOT * pop : lerp(DOT * 0.82, ICON, grow);
    const bob = -3 * idleMix.value * swell(idlePhase.value);
    return {
      opacity: t < BEAT.rise.start ? 0 : 1 - Math.min(1, out * 1.4),
      transform: [
        { translateY: lerp(ICON * 0.95, 0, rise) - out * 260 + bob },
        { rotate: `${lerp(14, 0, rise) - 10 * out}deg` },
        { scale: t >= BEAT.fill.start ? 0.9 : side / ICON },
      ],
    };
  });

  return (
    <View pointerEvents="none" style={styles.center}>
      {/* O quadrado recorta o "p": é assim que ele "sobe de dentro". */}
      <Animated.View style={[styles.shape, shapeStyle]}>
        <Animated.View style={markStyle}>
          <Image source={MARK_SOURCE} style={styles.mark} resizeMode="contain" fadeDuration={0} />
        </Animated.View>
      </Animated.View>
    </View>
  );
}

/** Movimento reduzido: o ícone parado no branco, e tudo some num fade. */
function StaticIcon() {
  return (
    <View pointerEvents="none" style={styles.center}>
      <View style={[styles.shape, { width: ICON, height: ICON, borderRadius: ICON * ICON_RADIUS }]}>
        <Image source={MARK_SOURCE} style={styles.mark} resizeMode="contain" fadeDuration={0} />
      </View>
    </View>
  );
}

// ─── Abertura: duas metades de borda curva ────────────────────────────────

function Split({ size, clock }: { size: Size; clock: SharedValue<number> }) {
  const { w, h } = size;
  const mid = h / 2;
  const cx = w / 2;

  const topProps = useAnimatedProps(() => {
    const t = clock.value;
    if (t < BEAT.splitBottom.start) return { d: 'M0 0' };
    const dy = -EASE_SPLIT(progress(t, BEAT.splitTop)) * (mid + SPLIT_BOW + BLEED);
    return {
      d: `M${-BLEED} ${-BLEED + dy}H${w + BLEED}V${mid + dy}Q${cx} ${mid + SPLIT_BOW + dy} ${-BLEED} ${mid + dy}Z`,
    };
  });

  const bottomProps = useAnimatedProps(() => {
    const t = clock.value;
    if (t < BEAT.splitBottom.start) return { d: 'M0 0' };
    const dy = EASE_SPLIT(progress(t, BEAT.splitBottom)) * (h - mid + BLEED);
    return {
      d: `M${-BLEED} ${mid + dy}Q${cx} ${mid + SPLIT_BOW + dy} ${w + BLEED} ${mid + dy}V${h + BLEED + dy}H${-BLEED}Z`,
    };
  });

  return (
    <Svg pointerEvents="none" style={StyleSheet.absoluteFill} width={w} height={h}>
      <AnimatedPath animatedProps={topProps} fill={BRAND} />
      <AnimatedPath animatedProps={bottomProps} fill={BRAND} />
    </Svg>
  );
}

const styles = StyleSheet.create({
  center: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shape: {
    backgroundColor: BRAND,
    overflow: 'hidden',
    alignItems: 'center',
    justifyContent: 'center',
  },
  mark: {
    width: ICON,
    height: ICON * MARK_ASPECT,
  },
});
